import React, { useState } from 'react';
import { styled, Theme, useMediaQuery, Slide } from '@mui/material';
import Toolbar from '@mui/material/Toolbar';
import AppBar from '@mui/material/AppBar';

import PopUpContactUs from '../../ContactUs/PopUpContactUs';

import Navigation from '../Navigation';
import { useScrollContext } from '../ScrollContext';

import { useHeader } from './HeaderProvider';
import { useHeaderSearch } from './HeaderSearchProvider';
import Logo from './Logo';
import BurgerMenu from '../BurgerMenu';
import { useRouter } from 'next/router';
import classnames from 'classnames';
import { useScrollController } from 'hooks/useScrollController';

export const HEADER_TRANSITION = {
  duration: 200,
  easing: 'cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

export const HEADER_HEIGHT = {
  desktop: 49,
  tablet: 65,
  mobile: 62,
};

export const HEADER_BORDER_COLOR = '#d2cfca';

const StyledAppBar = styled(AppBar)`
  background-color: transparent;
  box-shadow: none;
`;

const StyledToolbar = styled(Toolbar)`
  min-height: 0 !important;
  padding: 0 !important;
  background-color: #fff;
  width: 100%;
  border-bottom: 1px solid ${HEADER_BORDER_COLOR};
`;

interface HideOnScrollProps {
  children: React.ReactElement<React.HTMLAttributes<HTMLElement>>;
}

function HideOnScroll({ children }: HideOnScrollProps) {
  // Keep the header pinned while the pointer is over it, so an open dropdown
  // (e.g. Neighborhoods/Buildings) doesn't disappear when the user scrolls
  // through its content. The popups are DOM descendants of the header, so
  // hovering a popup counts as hovering the header for these handlers.
  const [isHovered, setIsHovered] = useState(false);
  const { shouldShow } = useScrollController({
    threshold: 100,
    paused: isHovered,
  });

  return (
    <Slide
      appear={false}
      direction="down"
      in={shouldShow}
      timeout={HEADER_TRANSITION.duration}
    >
      {React.cloneElement(children, {
        onMouseEnter: (event: React.MouseEvent<HTMLElement>) => {
          children.props.onMouseEnter?.(event);
          setIsHovered(true);
        },
        onMouseLeave: (event: React.MouseEvent<HTMLElement>) => {
          children.props.onMouseLeave?.(event);
          setIsHovered(false);
        },
      })}
    </Slide>
  );
}

interface HeaderProps {
  children: React.ReactNode;
  heightClass?: string;
  hideOnScroll?: boolean;
}

export const HeaderWrapper: React.FC<HeaderProps> = ({
  children,
  heightClass,
  hideOnScroll = false,
}) => {
  const header = (
    <StyledAppBar position="sticky">
      <div className={`w-full ${heightClass}`}>
        <StyledToolbar>{children}</StyledToolbar>
      </div>
    </StyledAppBar>
  );

  return hideOnScroll ? <HideOnScroll>{header}</HideOnScroll> : header;
};

// Create a stable component that doesn't depend on changing props
// Fixes the issue with the remounting of the component when the user searches
const StableDesktopMenu = React.memo(() => {
  const { fetchMoreBuildings, fetchMoreNhds, data } = useHeader();
  const {
    buildingSearch,
    setBuildingSearch,
    prevNeighborhoodSearch,
    setPrevNeighborhoodSearch,
    neighborhoodSearch,
    setNeighborhoodSearch,
  } = useHeaderSearch();
  const [menuActive, setMenuActive] = useState(false);

  return (
    <React.Fragment>
      <div className="flex items-center justify-between w-full lg:min-w-min">
        <Logo />
        <Navigation
          isDesktop
          hidden={'hidden xl:flex'}
          active={menuActive}
          setActive={setMenuActive}
          dataSet={{
            fetchMoreBuildings,
            fetchMoreNhds,
            data,
          }}
          searchInputData={{
            buildingSearch,
            setBuildingSearch,
            neighborhoodSearch,
            setNeighborhoodSearch,
            prevNeighborhoodSearch,
            setPrevNeighborhoodSearch,
          }}
        />
        <PopUpContactUs formLocation="header_popup" />
      </div>
    </React.Fragment>
  );
});
StableDesktopMenu.displayName = 'StableDesktopMenu';

const MobileMenu = ({ shouldShow }: { shouldShow?: boolean }) => {
  const { fetchMoreBuildings, fetchMoreNhds, data } = useHeader();
  const {
    buildingSearch,
    setBuildingSearch,
    neighborhoodSearch,
    setNeighborhoodSearch,
    prevNeighborhoodSearch,
    setPrevNeighborhoodSearch,
  } = useHeaderSearch();
  const [menuActive, setMenuActive] = useState(false);

  return (
    <React.Fragment>
      <div className="flex items-center">
        <BurgerMenu
          active={menuActive}
          setActive={setMenuActive}
          dataSet={{
            fetchMoreBuildings,
            fetchMoreNhds,
            data,
          }}
          searchInputData={{
            buildingSearch,
            setBuildingSearch,
            neighborhoodSearch,
            setNeighborhoodSearch,
            prevNeighborhoodSearch,
            setPrevNeighborhoodSearch,
          }}
        />
        <Logo />
      </div>
      <div className={shouldShow ? '' : 'invisible'}>
        <PopUpContactUs isMobile={true} formLocation="header_popup" />
      </div>
    </React.Fragment>
  );
};

export const DefaultHeaderBody: React.FC = () => {
  const { asPath } = useRouter();
  const isHomePage = asPath === '/';
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up('xl'));
  const scrollContext = useScrollContext();
  const shouldShow = scrollContext?.shouldShow ?? true;

  return (
    <div
      className={classnames(
        `
          flex max-w-full w-full items-center justify-between px-0 
          pl-[30px] s:pl-[20px] md:pl-[45px] lg:pl-[50px] 
          h-[61px] md:h-16
        `,
        {
          'container-header': isHomePage,
          container: !isHomePage,
        }
      )}
      style={{ height: isDesktop ? `${HEADER_HEIGHT.desktop}px` : undefined }}
    >
      {isDesktop ? (
        <StableDesktopMenu />
      ) : (
        <MobileMenu shouldShow={shouldShow} />
      )}
    </div>
  );
};

const Header: React.FC<{ hideOnScroll?: boolean }> = ({
  hideOnScroll = false,
}) => {
  return (
    <HeaderWrapper hideOnScroll={hideOnScroll}>
      <DefaultHeaderBody />
    </HeaderWrapper>
  );
};

export default Header;
